<template>
    <fieldable v-loading="contractsLoading"
               :label="label"
               :name="name"
               :required="required"
               :errors="errors"
               :no="independent"
               :dark="dark"
               class="contract-field">
        <!-- Label -->
        <template #label>
            <slot name="label" />
        </template>

        <!-- Field -->
        <div class="el-input__inner f-y-center min-w-11 h-1.75 fs-0.75 lh-1.75 cursor-pointer _mb-0.5 _px-0.25"
             @click="openContractsDialog">
            <!-- Field value -->
            <!-- for multi select by contracts -->
            <div v-if="withContractsSelection && value?.ast_contract_ids?.size"
                 class="f-y-center gap-0.25">
                <span v-for="c in astContracts.toSpliced(1)" 
                      :key="c.id"
                      class="el-tag el-tag--info el-tag--mini el-tag--light f-y-center border-0 max-w-15 truncate">
                    {{ getContractNumber(c) }} {{ getContractDescription(c) }}
                </span>
                <span v-if="value.ast_contract_ids.size > 1"
                      class="el-tag el-tag--info el-tag--mini el-tag--light f-y-center border-0">
                    +{{ value?.ast_contract_ids?.size - 1 }}
                </span>
            </div>

            <!-- for default select and multi select by works -->
            <div v-else-if="value?.ast_contract_id || (value?.ast_work_ids?.size && withWorksSelectionByContracts)"
                 class="el-tag el-tag--info el-tag--mini el-tag--light f-y-center border-0 max-w-15 truncate">
                {{ contractLabel }}
                <i v-if="!disabled && !astContractDisabled && !withWorksSelectionByContracts"
                   class="el-icon-close bg-gray-400 text-white"
                   @click.stop="handleClear" />
            </div>

            <!-- Placeholder -->
            <span v-else
                  class="text-gray-400 _px-0.75">
                {{ placeholder }}
            </span>
        </div>

        <!-- Tags and Ks2 switcher -->
        <template v-if="withKs2Enabled">
            <!-- Ks2 switcher -->
            <div v-if="!ks2SwitcherDisabled"
                 class="_p-0.75 bg-accent-0.2 rounded-lg _mb-0.5">
                <el-checkbox :value="ks2Enabled"
                             @change="$emit('change-ks2-enabled', $event)">
                    <span class="font-medium fs-0.75 _mr-0.25 text-el-dark">Учитывать объемы в КС-2</span>
                    <el-tooltip placement="right"
                                content="Сдаваемые объемы будут учитываться при заведении КС-2 в АСТ">
                        <i class="el-icon-info text-el-dark" />
                    </el-tooltip>
                </el-checkbox>
            </div>
            
            <!-- Tags -->
            <div v-else
                 class="space-x-0.5 _mb-0.5">
                <!-- Ks2 enabled tag -->
                <el-tag :type="ks2EnabledTag.tagType"
                        size="mini">
                    {{ ks2EnabledTag.name }}
                </el-tag>

                <!-- KS2 errors exists tag -->
                <el-tag v-if="withKs2Errors"
                        type="danger"
                        size="mini">
                    Ошибка в АСТ
                </el-tag>
            </div>
        </template>

        <!-- Work facts preview -->
        <div v-if="withContractsSelection && value && value.ast_contract_ids?.size"
             class="space-y-0.5">
            <contract-work-facts 
                v-for="c in astContracts"
                :key="'work-facts-' + c.id"
                :contract="c"
                :works="getContractWorks(c.works)"
                label-key="label"
                with-smeta-number
                with-list-label
                with-diff-count
                without-value />
            <el-button
                class="w-full"
                type="primary"
                size="mini"
                plain 
                @click="openContractsDialog">
                Раскрыть список
            </el-button>
        </div>

        <contract-work-facts v-else-if="withWorksSelection && value && !withWorksSelectionByContracts"
                             :task-id="taskId"
                             :works="works"
                             :contract="contract"
                             id-key="ast_work_id"
                             value-key="fact_in_units"
                             with-list-label
                             with-open-dialog
                             open-dialog-label="Раскрыть список" />
    </fieldable>
</template>

<script>
import { mapMutations } from 'vuex'

import { resourceable } from '@/store/connectors'

import { injectCommonFieldProps } from '@/utils/ui'
import { wordOfNumber } from '@/utils/format'

import { getContractNumber, getActualWorkFactUnits, getWorkName, getWorkNumber, getContractDescription } from '@/models/contracts'

import Fieldable from '@/components/fields/Fieldable'
import ContractWorkFacts from '@/components/contracts/ContractWorkFacts'

const labels = {
  label: 'Договор',
  placeholder: 'Выберите договор'
}

export default {
  name: 'ContractField',

  components: {
    Fieldable,
    ContractWorkFacts
  },
  
  mixins: [
    resourceable({ on: 'ast', name: 'contracts' }),
    resourceable({ on: 'ast', name: 'contractWorks' })
  ],

  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    ...injectCommonFieldProps({
      defaultLabel: labels.label, 
      defaultPlaceholder: labels.placeholder 
    }),

    value: { type: [Object, Array, String], default: null },
    workErrors: { type: Object, default: () => ({}) },
    ks2Enabled: { type: Boolean, default: false },

    dark: { type: Boolean, default: false },

    astContract: { type: Object, default: null },
    taskId: { type: String, default: null },
    contractorId: { type: String, default: null },

    withContractSearch: { type: Boolean, default: false },

    withWorksSelectionByContracts: { type: Boolean, default: false },
    withContractsSelection: { type: Boolean, default: false },
    withWorks: { type: Boolean, default: false },
    withWorksSelection: { type: Boolean, default: false },
    withWorkCheckbox: { type: Boolean, default: true },
    withKs2Enabled: { type: Boolean, default: false },
    withKs2Errors: { type: Boolean, default: false },

    ks2SwitcherDisabled: { type: Boolean, default: false },
    astContractDisabled: { type: Boolean, default: false },
    astWorkFactsDisabled: { type: Boolean, default: false },

    editableWorkFactsField: { type: Array, default: () => [] },

    withRejectionsTaskSource: { type: Boolean, default: false },

    rejectionable: { type: Boolean, default: false },
    creatable: { type: Boolean, default: false }
  },

  data() {
    return {
      contractsData: [],

      getContractNumber,
      getContractDescription
    }
  },

  computed: {
    contract() {
      return this.value && this.contractsData.find(x => x.id === this.value.ast_contract_id) || this.astContract
    },

    contractLabel() {
      return this.value?.ast_contract_id && !this.withWorksSelectionByContracts
        ? this.contractName
        : this.withWorksSelectionByContracts && this.value?.ast_work_ids?.size
          ? `${this.value.ast_work_ids.size} ${wordOfNumber(this.value.ast_work_ids.size, ['позиция', 'позиции', 'позиций'])}`
          : 'Неизвестно'
    },

    contractName() {
      return this.value && `${getContractNumber(this.contract)} ${getContractDescription(this.contract)}` || ''
    },

    works() {
      return this.value && this.value.ast_work_facts?.map(x => {
        const work = this.contractWorks.find(y => y.id === x.ast_work_id)
        const label = `${getWorkNumber(work)} ${getWorkName(work)}`
        const fact_in_units = x.fact_in_units || getActualWorkFactUnits(work)
        const blocked_at = work?.blocked_at

        return { ...x, fact_in_units, label, unit_name: work?.data?.unit_name, blocked_at }
      }) || []
    },

    hasContent() {
      return this.astContract || !!this.contractsData.length
    },

    ks2EnabledTag() {
      return this.ks2Enabled 
        ? { name: 'Объемы учитываются в КС-2', tagType: 'success' }
        : { name: 'Без учета объемов', tagType: 'warning' }
    },

    astContracts() {
      return this.value && this.value?.ast_contract_ids?.size && this.withContractsSelection 
        ? this.contractsData
          .filter((({ id }) => this.value.ast_contract_ids.has(id)))
          .map(c => ({ ...c, works: c.works.filter(w => this.value?.ast_work_ids?.has(w.id)) })) 
        : []
    }
  },

  watch: {
    contractorId(x) {
      x && this.loadContracts()
    }
  },

  async mounted() {
    this.withKs2Enabled && !this.readonly && this.$emit('change-ks2-enabled', this.ks2Enabled)
    
    await this.loadContracts()
    this.init()
  },

  methods: {
    ...mapMutations({
      showDialog: 'dialog/SHOW_DIALOG'
    }),

    init() {
      if (this.value && !this.withWorksSelectionByContracts) {
        const projectId = this.$route.params.projectId
        const contractId = this.contract?.id

        contractId && this.fetchContractWorks({ 
          projectId, 
          contractId, 
          taskId: this.taskId, 
          withWorkType: true, 
          withFactInUnitsAll: true, 
          withFactInUnitsApproved: true, 
          withFactInUnitsCreated: true, 
          withFactInUnitsFirstStage: true, 
          withFactInUnitsSecondStage: true,
          withBlockedWorks: !!this.taskId,
          withKs2Errors: !!this.taskId,

          withRejectionsTaskSource: this.withRejectionsTaskSource,

          sort: 'smeta_number'
        })
      }
    },

    loadContracts() {
      return this.fetchContracts({ 
        projectId: this.$route.params.projectId, 
        contractorId: this.contractorId,
        withOrganization: true,
        ...this.withWorksSelectionByContracts && {
          withWorks: true,
          withWorkType: true,
          compact: true
        }
      }).then((data) => {
        this.contractsData = data

        if (this.withContractsSelection && this.value.ast_work_ids?.size && !this.value.ast_contract_ids?.size) {
          const editedItem = structuredClone(this.value)

          this.contractsData.forEach(contract => {        
            if (contract.works?.find(w => editedItem.ast_work_ids.has(w.id))) {
              editedItem.ast_contract_ids.set(contract.id, true)
            }
          })

          this.handleChange(editedItem)
        }

        return data
      })
    },

    handleClear() {
      this.$emit('change', null)
    },

    handleChange(value) {
      this.$emit('change', value)
    },

    openContractsDialog() {
      this.showDialog({
        dialogName: 'contracts',
        action: this.handleChange,
        payload: {
          contracts: this.contractsData,

          editedItem: this.value,
          contract: this.contract,
          taskId: this.taskId,
          workErrors: this.workErrors,

          withContractSearch: this.withContractSearch,
          readonly: this.readonly || this.disabled,
          astContractDisabled: this.astContractDisabled,
          astWorkFactsDisabled: this.astWorkFactsDisabled,

          withWorksSelectionByContracts: this.withWorksSelectionByContracts,
          withContractsSelection: this.withContractsSelection,
          withWorks: this.withWorks,
          withWorksSelection: this.withWorksSelection || this.withWorksSelectionByContracts,
          withWorkCheckbox: this.withWorkCheckbox,
          withKs2Errors: this.withKs2Errors,

          editableWorkFactsField: this.editableWorkFactsField,

          withRejectionsTaskSource: this.withRejectionsTaskSource,
          rejectionable: this.rejectionable,
          creatable: this.creatable
        },
        multiple: true
      })
    },

    getContractWorks(works) {
      return works.map((x) => ({ ...x, label: `${getWorkNumber(x)} ${getWorkName(x)}`}))
    }
  }
}
</script>

<style lang="scss">
.contract-field {
  .work_item:not(:first-child) {
    border-top: 1px solid #DCDFE6;
  }
}
</style>
